import {
	ReactElement,
	useCallback
} from "react";
import { useRenderManager } from "../useRenderManager";
import { TwoByTwoTileContent } from "./TwoByTwoTileContent";
import { OneByOneTileContent } from "./OneByOneTileContent";
import { navigateToWindowsVirtualDesktopApp } from "client_legacy/Apps/appNavigation";
import { AppType } from "client/Api/Apps/common";
import { TileRenderState } from "../RenderState/common";
import {
	useBaseNavigation,
	useConditionalAccessNavigation,
	useMaintenanceWindowNavigation
} from "./useInvokeNavigation";
import { TimeZone } from "client/DateAndTime/timezones";
import { TileContextProvider } from "../TileContextProvider";
import { useRegisterTileUsage } from "./useRegisterTileUsage";
import { Coordinate } from "client/Misc/commonTypes";

export type WindowsVirtualDesktopTileContentProps = {
	renderState: TileRenderState<AppType.WindowsVirtualDesktop>;
	onEdit: (() => void) | undefined;
	userTimezone: TimeZone;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const WindowsVirtualDesktopTileContent = (props: WindowsVirtualDesktopTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		userTimezone,
		onOpenTileMenu
	} = props;

	const [navigationElement, navigateHandler] = useWindowsVirtualDesktopTileNavigation(
		renderState,
		onEdit,
		userTimezone
	);

	const { dimension } = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<TwoByTwoTileContent
				onClick={navigateHandler}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
WindowsVirtualDesktopTileContent.displayName = "WindowsVirtualDesktopTileContent";

export function useWindowsVirtualDesktopTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.WindowsVirtualDesktop>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined,
	userTimezone: TimeZone
): [element: ReactElement | null, () => void | Promise<void>] {
	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);

	const {
		conditionalAccess,
		maintenanceWindow,
		environmentAppId,
		settings: {
			clientKind
		}
	} = renderStateIsh.data;

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useConditionalAccessNavigation(
			conditionalAccess,
			useMaintenanceWindowNavigation(
				maintenanceWindow,
				userTimezone,
				useCallback(
					(): void => {
						registerTileUsage();

						navigateToWindowsVirtualDesktopApp({
							id: environmentAppId,
							client: clientKind
						});
					},
					[
						clientKind,
						environmentAppId,
						registerTileUsage
					]
				)
			)
		)
	);
}
